/**
 * Two-tier cache store: in-memory L1 + IndexedDB L2.
 *
 * Why two tiers:
 *   - L1 (in-memory `Map`) gives sub-millisecond hits within the same tab —
 *     critical for repeat reads inside a single render cycle (e.g. opening a
 *     recipe re-renders 5–10 times before all panes settle, and each render
 *     can issue the same `recipe.get` from a different effect).
 *   - L2 (IndexedDB via `idb-keyval`) survives navigation and tab restarts.
 *     This is what closes the "warm second-open" gap (~300–800 ms → ~10–20 ms).
 *
 * Both tiers store the same shape: `CacheEntry<T> = { data, storedAt }`. The
 * SWR layer reads `storedAt` to decide fresh vs stale.
 *
 * Failure policy (Rule 8):
 *   IDB calls are wrapped in try/catch. On failure we `console.warn` and
 *   degrade to L1-only. This is best-effort caching; the user-visible read
 *   path always falls through to the network if the cache cannot serve.
 *   Mutations are NOT routed through this layer (they go straight to the
 *   network), so a degraded cache cannot drop user writes.
 *
 * Ref: tasks/pr-body-p4-followup-3.md Path A.
 */

import {
  get as idbGet,
  set as idbSet,
  del as idbDel,
  keys as idbKeys,
  delMany as idbDelMany,
  clear as idbClear,
} from "idb-keyval";

export interface CacheEntry<T> {
  /** The cached payload (output of the RPC procedure). */
  data: T;
  /** Wall-clock timestamp at write, in ms since epoch. */
  storedAt: number;
}

// In-memory mirror. Keyed identically to IDB. Bounded only by working-set
// size; the SWR layer never holds more entries than the live UI references,
// so an unbounded Map is safe in practice. (10 recipes × 4 procedures × ~5 KB
// per entry = ~200 KB working set — well under any plausible quota.)
const L1 = new Map<string, CacheEntry<unknown>>();

/**
 * Read the L1 mirror first (synchronous-fast), then fall back to IDB. Returns
 * `null` on miss or on IDB failure (best-effort).
 */
export async function get<T>(key: string): Promise<CacheEntry<T> | null> {
  const l1 = L1.get(key);
  if (l1 !== undefined) {
    return l1 as CacheEntry<T>;
  }
  try {
    const l2 = await idbGet(key);
    if (l2 === undefined || l2 === null) return null;
    // Defensive shape check — a corrupt or rehydrated entry from an older
    // schema should be treated as a miss, not crash the app.
    if (
      typeof l2 !== "object" ||
      typeof (l2 as { storedAt?: unknown }).storedAt !== "number" ||
      !("data" in (l2 as object))
    ) {
      return null;
    }
    const entry = l2 as CacheEntry<T>;
    L1.set(key, entry);
    return entry;
  } catch (err) {
    console.warn("[proof.cache] IDB get failed; degrading to L1-only", { key, err });
    return null;
  }
}

/**
 * Write to both L1 (sync) and IDB (async, fire-and-forget on the IDB side
 * from the caller's perspective — but we still await it so failures surface).
 */
export async function set<T>(key: string, value: T): Promise<void> {
  const entry: CacheEntry<T> = { data: value, storedAt: Date.now() };
  L1.set(key, entry);
  try {
    await idbSet(key, entry);
  } catch (err) {
    console.warn("[proof.cache] IDB set failed; entry held in L1 only", { key, err });
  }
}

export async function del(key: string): Promise<void> {
  L1.delete(key);
  try {
    await idbDel(key);
  } catch (err) {
    console.warn("[proof.cache] IDB del failed", { key, err });
  }
}

/**
 * Delete every cache entry whose key starts with `prefix`. Used by
 * `invalidate(procedurePrefix)` and `invalidateAll(userPrefix)`.
 *
 * Implementation: idb-keyval has no native prefix-scan, so we list all keys
 * and filter. This is O(n) over the cache, but n is bounded (a typical
 * library has <100 cached entries). If profiling later shows this is hot,
 * switch to a custom IDB store with an index.
 */
export async function delByPrefix(prefix: string): Promise<void> {
  // L1 walk first so synchronous follow-up reads see the invalidation.
  for (const key of Array.from(L1.keys())) {
    if (key.startsWith(prefix)) L1.delete(key);
  }
  try {
    const allKeys = await idbKeys();
    const matching = allKeys.filter(
      (k): k is string => typeof k === "string" && k.startsWith(prefix),
    );
    if (matching.length > 0) {
      await idbDelMany(matching);
    }
  } catch (err) {
    console.warn("[proof.cache] IDB delByPrefix failed", { prefix, err });
  }
}

/**
 * Clear every cache entry across every user. Reserved for diagnostics /
 * reset paths; sign-out should prefer `delByPrefix(userPrefix(...))` so other
 * users (multi-account future) are unaffected.
 */
export async function clear(): Promise<void> {
  L1.clear();
  try {
    await idbClear();
  } catch (err) {
    console.warn("[proof.cache] IDB clear failed", { err });
  }
}

/**
 * Test-only helper: drop the L1 mirror without touching IDB. Lets unit tests
 * exercise the L2 hydration path deterministically.
 */
export function __resetL1ForTest(): void {
  L1.clear();
}
